import React, {useEffect, useState} from 'react';
import {getSongsByApple} from '../../libraries/DnaApi';
import {likePlaylistUrl} from '../../libraries/MusicKit';

const FALLBACK_COVER = '/images/cover-placeholder.jpg';

/**
 * The likes, read live from the user's Apple Music playlist "Discovered by
 * AbTunes.com". Newest first. A row plays when we've analyzed that exact
 * track (it becomes the engine's starting point). Removal happens in Apple
 * Music: Apple's API can't take songs out of a playlist.
 */
export default function LikedDrawer({open, onClose, liked, onRefresh, onConnect, onPlay}) {
    const [songsByApple, setSongsByApple] = useState({});

    useEffect(() => {
        if (open) {
            onRefresh();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const tracks = liked?.tracks || [];
    const idsKey = tracks.map((t) => t.apple_id).filter(Boolean).join(',');
    useEffect(() => {
        if (!open || !idsKey) {
            return;
        }
        let active = true;
        getSongsByApple(idsKey.split(',')).then((songs) => {
            if (active) {
                setSongsByApple(Object.fromEntries(songs.map((s) => [String(s.apple_id), s])));
            }
        });
        return () => {
            active = false;
        };
    }, [open, idsKey]);

    if (!open) {
        return null;
    }

    const rows = [...tracks].reverse();

    return (
        <div className="fixed inset-0 z-40">
            <div className="absolute inset-0 bg-black/50" onClick={onClose}/>
            <div className="absolute right-0 top-0 flex h-full w-[88%] max-w-[360px] flex-col bg-white/[0.07] shadow-[inset_1px_0_0_rgba(255,255,255,0.14),-30px_0_70px_rgba(0,0,0,0.5)] backdrop-blur-3xl">
                <div className="flex flex-none items-start justify-between gap-3 px-5 pb-3 pt-5">
                    <div>
                        <div className="text-[13px] font-extrabold uppercase tracking-[0.08em] text-white/40">Your playlist</div>
                        <div className="mt-0.5 text-[11.5px] text-white/[0.35]">Discovered by AbTunes.com, in your Apple Music</div>
                    </div>
                    <button type="button" onClick={onClose} title="Close"
                            className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-white/10 text-white/90 transition hover:bg-white/20">
                        <i className="fa-solid fa-xmark"/>
                    </button>
                </div>

                <div className="scrollbar-subtle min-h-0 flex-1 overflow-y-auto px-3 pb-4">
                    {liked === null && (
                        <div className="px-2 pt-4">
                            <p className="text-[13px] text-white/50">
                                Connect Apple Music and every song you add lands in a playlist you keep.
                            </p>
                            <button type="button" onClick={onConnect}
                                    className="mt-4 rounded-xl bg-apple-red px-4 py-2.5 text-[13px] font-bold">
                                Connect Apple Music
                            </button>
                        </div>
                    )}
                    {liked === undefined && <p className="px-2 pt-4 text-[13px] text-white/40">Loading…</p>}
                    {liked && rows.length === 0 && (
                        <p className="px-2 pt-4 text-[13px] text-white/40">
                            Nothing yet. Tap + on a song you love and it lands here.
                        </p>
                    )}
                    {rows.map((t, i) => {
                        const song = t.apple_id ? songsByApple[String(t.apple_id)] : null;
                        return (
                            <button
                                key={(t.apple_id || t.title) + i}
                                type="button"
                                disabled={!song}
                                onClick={() => { onPlay(song); onClose(); }}
                                title={song ? 'Start from this song' : "We haven't analyzed this exact track yet"}
                                className="flex w-full items-center gap-3 rounded-xl p-2 text-left transition enabled:hover:bg-white/[0.08] disabled:opacity-40"
                            >
                                <img src={t.cover || song?.cover || FALLBACK_COVER} alt="" loading="lazy"
                                     className="h-10 w-10 flex-none rounded-lg object-cover"/>
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-[13px] font-semibold">{t.title}</span>
                                    <span className="block truncate text-[11.5px] text-white/[0.45]">{t.artist}</span>
                                </span>
                            </button>
                        );
                    })}
                </div>

                {liked?.playlistId && (
                    <div className="flex-none px-5 pb-5 pt-2">
                        <a href={likePlaylistUrl(liked.playlistId)} target="_blank" rel="noreferrer"
                           className="flex items-center justify-center gap-2 rounded-xl bg-white/[0.08] py-2.5 text-[12.5px] font-semibold text-white/75 transition hover:bg-white/[0.14] hover:text-white">
                            <i className="fa-brands fa-apple text-[14px]"/> Edit or remove in Apple Music
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
}
